import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getClinic, getClinicServices } from '../api/client';
import Footer from '../components/Footer';

export default function ClinicPage() {
  const { clinicId } = useParams();
  const navigate = useNavigate();
  const [clinic, setClinic] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        const [clinicResponse, servicesResponse] = await Promise.all([
          getClinic(clinicId),
          getClinicServices(clinicId)
        ]);
        
        setClinic(clinicResponse.clinic);
        setServices(servicesResponse.services || []);
        setError(null);
      } catch (err) {
        console.error('Ошибка загрузки данных клиники:', err);
        setError('Не удалось загрузить информацию о клинике. Пожалуйста, попробуйте позже.');
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [clinicId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-red-500 text-center">
          <p className="text-xl font-semibold mb-2">Ошибка</p>
          <p>{error}</p>
          <button 
            onClick={() => navigate('/')}
            className="mt-4 px-4 py-2 bg-secondary text-white rounded-md hover:bg-secondary/90 transition"
          >
            Вернуться на главную
          </button>
        </div>
      </div>
    );
  }

  if (!clinic) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-xl font-semibold mb-2">Клиника не найдена</p>
          <button 
            onClick={() => navigate('/')}
            className="mt-4 px-4 py-2 bg-secondary text-white rounded-md hover:bg-secondary/90 transition"
          >
            Вернуться на главную
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 text-gray-800 relative pb-16">
      <header className="bg-white shadow p-4">
        <div className="flex items-center gap-4 max-w-3xl mx-auto relative">
          <button
            onClick={() => navigate('/')}
            className="absolute left-0 text-gray-600 hover:bg-gray-100 rounded-full w-10 h-10 flex items-center justify-center transition"
          >
            ←
          </button>
          <div className="flex items-center gap-4 w-full justify-center">
            <img
              src={clinic.logo || '/logo.png'}
              alt={clinic.name}
              className="w-16 h-16 rounded-full object-cover bg-primary/10 border-2 border-white shadow"
            />
            <div>
              <h1 className="text-xl font-semibold">{clinic.name}</h1>
              <div className="flex items-center text-sm text-gray-500 gap-1">
                <svg className="w-4 h-4 text-primary" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22S19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5Z" />
                </svg>
                <span>{clinic.city}, {clinic.address}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-center gap-3 mt-4 max-w-3xl mx-auto flex-wrap">
          {clinic.contacts?.phones?.map((phone, index) => (
            <a
              key={index}
              href={`tel:${phone}`}
              className="px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium hover:bg-primary/20 transition"
            >
              📞 {phone}
            </a>
          ))}
          <a
            href={`https://maps.google.com/?q=${encodeURIComponent(clinic.address)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium hover:bg-primary/20 transition"
          >
            🗺️ Маршрут
          </a>
          {clinic.website && (
            <a
              href={clinic.website}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium hover:bg-primary/20 transition"
            >
              🌐 Сайт
            </a>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6">
        <h2 className="text-lg font-semibold mb-4">Услуги</h2>
        <div className="flex flex-col gap-3">
          {services.map((service) => (
            <a
              key={service.id}
              href={`/${clinic.id}/${service.id}`}
              className="p-4 bg-white rounded-xl shadow flex justify-between items-center hover:bg-gray-50 transition"
            >
              <div>
                <div className="font-medium">{service.name}</div>
                {service.price && (
                  <div className="text-sm text-gray-500">от {service.price} ₼</div>
                )}
                {service.description && (
                  <div className="text-sm text-gray-500 mt-1">{service.description}</div>
                )}
              </div>
              <span className="text-2xl text-secondary">›</span>
            </a>
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
}
